import React, { useState, useEffect } from "react";

import { CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { ChevronDown, ArrowLeft } from "lucide-react";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

import BankSelect from "./BankSelect";

export default function WithdrawAddBank({
  userId,
  onConfirm,
  onBack,
  formState,
  onFormStateChange,
}) {
  const [localSelectedBank, setLocalSelectedBank] = useState(
    formState?.selectedBank ?? null
  );
  const [localNewBankType, setLocalNewBankType] = useState(
    formState?.newBankType ?? ""
  );
  const [localNewAccountNumber, setLocalNewAccountNumber] = useState(
    formState?.newAccountNumber ?? ""
  );
  const [localNewBranchCode, setLocalNewBranchCode] = useState(
    formState?.newBranchCode ?? ""
  );

  useEffect(() => {
    if (formState) {
      if (formState.selectedBank !== undefined) {
        setLocalSelectedBank(formState.selectedBank);
      }
      if (formState.newBankType !== undefined) {
        setLocalNewBankType(formState.newBankType);
      }
      if (formState.newAccountNumber !== undefined) {
        setLocalNewAccountNumber(formState.newAccountNumber);
      }
      if (formState.newBranchCode !== undefined) {
        setLocalNewBranchCode(formState.newBranchCode);
      }
    }
  }, [formState]);

  const isControlled = formState !== undefined && formState !== null;
  const selectedBank = isControlled
    ? formState.selectedBank !== undefined
      ? formState.selectedBank
      : null
    : localSelectedBank;
  const newBankType = isControlled
    ? formState.newBankType !== undefined
      ? formState.newBankType
      : ""
    : localNewBankType;
  const newAccountNumber = isControlled
    ? formState.newAccountNumber !== undefined
      ? formState.newAccountNumber
      : ""
    : localNewAccountNumber;
  const newBranchCode = isControlled
    ? formState.newBranchCode !== undefined
      ? formState.newBranchCode
      : ""
    : localNewBranchCode;

  const [open, setOpen] = useState(false);
  const [, setStep] = useState("");
  const [errors, setErrors] = useState({
    bankName: "",
    accountNumber: "",
    branchCode: "",
    bankType: "",
  });

  const bankArray = {
    bankName: selectedBank?.name || selectedBank?.bankName,
    accNumber: newAccountNumber,
    branchCode: newBranchCode || selectedBank?.universalBranchCode,
    bankId: selectedBank?._id || selectedBank?.bankId,
    accType: newBankType
      ? newBankType.charAt(0).toUpperCase() + newBankType.slice(1).toLowerCase()
      : newBankType,
    userId: userId,
  };

  const clearError = (fieldName) => {
    setErrors((prev) => ({
      ...prev,
      [fieldName]: "",
    }));
  };

  const validateForm = () => {
    const newErrors = {
      bankName: "",
      accountNumber: "",
      branchCode: "",
      bankType: "",
    };

    let isValid = true;

    if (!selectedBank || (!selectedBank._id && !selectedBank.bankId)) {
      newErrors.bankName = "Please select a bank.";
      isValid = false;
    }

    const accountNumberTrimmed = newAccountNumber?.trim() || "";
    if (!accountNumberTrimmed) {
      newErrors.accountNumber = "Please enter an account number.";
      isValid = false;
    } else if (!/^\d+$/.test(accountNumberTrimmed)) {
      newErrors.accountNumber = "Account number must contain only numbers.";
      isValid = false;
    } else if (
      accountNumberTrimmed.length < 8 ||
      accountNumberTrimmed.length > 12
    ) {
      newErrors.accountNumber =
        "Account number must be between 8 and 12 digits.";
      isValid = false;
    }

    const branchCodeTrimmed = newBranchCode?.trim() || "";
    if (!branchCodeTrimmed) {
      newErrors.branchCode = "Please enter a branch code.";
      isValid = false;
    } else if (!/^\d+$/.test(branchCodeTrimmed)) {
      newErrors.branchCode = "Branch code must contain only numbers.";
      isValid = false;
    } else if (branchCodeTrimmed.length < 4 || branchCodeTrimmed.length > 6) {
      newErrors.branchCode = "Branch code must be between 4 and 6 digits.";
      isValid = false;
    }

    if (!newBankType) {
      newErrors.bankType = "Please select a bank type.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleContinue = () => {
    if (validateForm()) {
      setStep("confirm");
      if (onConfirm) {
        onConfirm(bankArray);
      }
    }
  };

  const handleSelecedBank = (bank) => {
    const updates = { selectedBank: bank };
    if (bank?.universalBranchCode) {
      updates.newBranchCode = bank.universalBranchCode;
      clearError("branchCode");
    }
    if (onFormStateChange) {
      onFormStateChange(updates);
    } else {
      setLocalSelectedBank(bank);
      if (bank?.universalBranchCode) {
        setLocalNewBranchCode(bank.universalBranchCode);
      }
    }
    clearError("bankName");
  };

  const handleAccountNumberChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      if (onFormStateChange) {
        onFormStateChange({ newAccountNumber: value });
      } else {
        setLocalNewAccountNumber(value);
      }
      if (value && value.trim() !== "") {
        const trimmed = value.trim();
        if (!/^\d+$/.test(trimmed)) {
          setErrors((prev) => ({
            ...prev,
            accountNumber: "Account number must contain only numbers.",
          }));
        } else if (trimmed.length < 8 || trimmed.length > 12) {
          setErrors((prev) => ({
            ...prev,
            accountNumber: "Account number must be between 8 and 12 digits.",
          }));
        } else {
          clearError("accountNumber");
        }
      } else {
        clearError("accountNumber");
      }
    }
  };

  const handleBranchCodeChange = (e) => {
    const value = e.target.value;
    if (value === "" || (/^\d+$/.test(value) && value.length <= 6)) {
      if (onFormStateChange) {
        onFormStateChange({ newBranchCode: value });
      } else {
        setLocalNewBranchCode(value);
      }
      if (value && value.trim() !== "") {
        const trimmed = value.trim();
        if (!/^\d+$/.test(trimmed)) {
          setErrors((prev) => ({
            ...prev,
            branchCode: "Branch code must contain only numbers.",
          }));
        } else if (trimmed.length < 4 || trimmed.length > 6) {
          setErrors((prev) => ({
            ...prev,
            branchCode: "Branch code must be between 4 and 6 digits.",
          }));
        } else {
          clearError("branchCode");
        }
      } else {
        clearError("branchCode");
      }
    }
  };

  const handleBankTypeSelect = (type) => {
    if (onFormStateChange) {
      onFormStateChange({ newBankType: type });
    } else {
      setLocalNewBankType(type);
    }
    setOpen(false);
    clearError("bankType");
  };

  const isFormValid = () => {
    const branchCodeTrimmed = newBranchCode?.trim() || "";
    const branchCodeValid =
      branchCodeTrimmed &&
      /^\d+$/.test(branchCodeTrimmed) &&
      branchCodeTrimmed.length >= 4 &&
      branchCodeTrimmed.length <= 6;

    const isValid =
      selectedBank &&
      (selectedBank._id || selectedBank.bankId) &&
      newAccountNumber?.trim() &&
      /^\d+$/.test(newAccountNumber.trim()) &&
      newAccountNumber.trim().length >= 8 &&
      newAccountNumber.trim().length <= 12 &&
      branchCodeValid &&
      newBankType;
    return isValid;
  };
  return (
    <CardContent className="md:px-6 px-2">
      <h2 className="text-[20px] font-semibold text-[#6D6584] mb-2">
        Add a bank account
      </h2>
      <p className="text-[16px] text-[#6D6584] mb-6 font-normal">
        We'll withdraw ZAR from your Digital Wallet and deposit it into this
        account. Please use your own bank account. Cash deposits are not
        allowed.
      </p>

      <div className="mb-4">
        <label className="block text-[14px] font-normal text-[#6D6584] mb-2">
          Bank name
        </label>
        <BankSelect
          onSelect={handleSelecedBank}
          isWithdraw={false}
          flagSelectedBank={!!errors.bankName}
          parentSelectedBank={selectedBank}
        />
        {errors.bankName && (
          <p className="mt-2 text-sm text-rose-500">{errors.bankName}</p>
        )}
      </div>

      <div className="mb-4">
        <label className="block text-[14px] font-normal text-[#6D6584] mb-2">
          Account number
        </label>
        <Input
          type="text"
          inputMode="numeric"
          placeholder="e.g. 1234567890"
          value={newAccountNumber}
          onChange={handleAccountNumberChange}
          className={`h-[56px] ${
            errors.accountNumber
              ? "border-rose-500 border-[1px] focus-visible:ring-rose-500 focus-visible:ring-offset-0"
              : "border-[#CECBD7]"
          }`}
        />
        {errors.accountNumber && (
          <p className="mt-2 text-sm text-rose-500">{errors.accountNumber}</p>
        )}
      </div>

      <div className="mb-4">
        <label className="block text-[14px] font-normal text-[#6D6584] mb-2">
          Branch code
        </label>
        <Input
          type="text"
          inputMode="numeric"
          placeholder="e.g. 051001"
          value={newBranchCode}
          onChange={handleBranchCodeChange}
          maxLength={6}
          minLength={4}
          className={`h-[56px] ${
            errors.branchCode
              ? "border-rose-500 border-[1px] focus-visible:ring-rose-500 focus-visible:ring-offset-0"
              : "border-[#CECBD7]"
          }`}
        />
        {errors.branchCode && (
          <p className="mt-2 text-sm text-rose-500">{errors.branchCode}</p>
        )}
      </div>

      <div className="mb-6">
        <label className="block text-[14px] font-normal text-[#6D6584] mb-2">
          Bank type
        </label>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              className={`w-full h-[56px] flex items-center justify-between py-8 rounded-2xl hover:border-brand-primary transition-colors text-[14px] font-normal ${
                errors.bankType
                  ? "border-rose-500 border-[1px]"
                  : "border-[#CECBD7]"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="text-left">
                  <div
                    className={`font-semibold ${
                      newBankType ? "capitalize" : ""
                    } ${errors.bankType ? "text-rose-500" : "text-[#6D6584]"}`}
                  >
                    {newBankType || "Select a bank type"}
                  </div>
                </div>
              </div>
              <ChevronDown
                className={errors.bankType ? "text-rose-500" : "text-[#6D6584]"}
              />
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Select a bank type</DialogTitle>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              {/* Bank Type Selection */}
              <div className="flex gap-4">
                <button
                  onClick={() => handleBankTypeSelect("savings")}
                  className={`p-4 flex-1 text-center rounded-lg border ${
                    newBankType === "savings"
                      ? "bg-blue-500 text-white border-blue-500"
                      : "border-gray-300 hover:bg-gray-100"
                  }`}
                >
                  Savings Account
                </button>

                <button
                  onClick={() => handleBankTypeSelect("cheque")}
                  className={`p-4 flex-1 text-center rounded-lg border ${
                    newBankType === "cheque"
                      ? "bg-blue-500 text-white border-blue-500"
                      : "border-gray-300 hover:bg-gray-100"
                  }`}
                >
                  Cheque Account
                </button>

                <button
                  onClick={() => handleBankTypeSelect("transmission")}
                  className={`p-4 flex-1 text-center rounded-lg border ${
                    newBankType === "transmission"
                      ? "bg-blue-500 text-white border-blue-500"
                      : "border-gray-300 hover:bg-gray-100"
                  }`}
                >
                  Transmission Account
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {errors.bankType && (
          <p className="mt-2 text-sm text-rose-500">{errors.bankType}</p>
        )}
      </div>

      <div className="flex items-center justify-center flex-col md:flex-row md:gap-0 gap-4 mt-8">
        <div className="lg:px-6 lg:py-6 md:flex items-center hidden">
          <Button
            variant="ghost"
            onClick={onBack}
            className="w-10 h-10 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
          >
            <ArrowLeft size={20} className="text-gray-700" />
          </Button>
        </div>
        <Button
          onClick={handleContinue}
          disabled={!isFormValid()}
          className={`px-6 py-4 rounded-full font-semibold transition-opacity w-full sm:min-w-[302px] max-w-sm h-[48px] text-sm sm:text-base ${
            !isFormValid()
              ? "bg-[#F2F1F4] text-[#9D96AF] cursor-not-allowed"
              : "bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] text-white hover:opacity-90"
          }`}
        >
          Continue
        </Button>
        <Button
          variant="ghost"
          onClick={onBack}
          className="md:hidden block w-full h-[48px] rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100 order-2 lg:order-1 mt-2"
        >
          Back
        </Button>
      </div>
    </CardContent>
  );
}
